import {
  FlowNodeEntity, WorkflowNodeLinesData, FreeLayoutPluginContext,
  FlowNodeBaseType,
} from '@flowgram.ai/free-layout-editor';
import useFlowStore, { IExecuteWorkflowItem } from '$/store/useFlowStore';
import { FlowNodeTypeEnum } from '$/domains/node/type';
import { editNodeFlowType, inputNodeFlowType } from '../NodeAddPanel/components/NodeAdd/constant';

interface EditNodeOptions {
  inPublish?: boolean;
}

/**
 *  判断两组索引是否有重复。重复的合并返回。不重复的返回第一个
 *
 * @param {number[]} a
 * @param {number[]} b
 * @return {*}
 */
function hasRepeat(a: number[], b: number[]) {
  const arr = [...a, ...b];
  const set = new Set(arr);
  if (set.size === arr.length) {
    return a;
  }
  return Array.from(set);
}

/**
 * 找出两个索引数组的交集
 *
 * @param {number[][]} list
 * @param {number[]} item
 * @param {number} i
 * @return {*}
 */
function getRepeatSortStr(list: number[][], item: number[], i: number) {
  const repeatList = list.reduce((total: number[], cur: number[], index: number) => {
    if (i === index) {
      return total;
    }
    return hasRepeat(total, cur);
  }, item);

  return repeatList.sort().join(',');
}

/**
 * 获取重复索引数组
 *
 * @export
 * @param {Record<string, number[]>} idMap
 * @return {*}
 */
export function getRepeatList(idMap: Record<string, number[]>) {
  const res = new Set<string>();
  const list: number[][] = [];
  for (const id in idMap) {
    if (Object.prototype.hasOwnProperty.call(idMap, id)) {
      const indexs = idMap[id];
      list.push(indexs);
    }
  }

  list.forEach((item, i) => {
    res.add(getRepeatSortStr(list, item, i));
  });

  return res;
}

/**
 * 如果多个工作流有交集，需要合并交集的工作流为一个工作流
 *
 * @export
 * @param {FreeLayoutPluginContext} ctx
 * @param {((FlowNodeEntity | FlowNodeEntity[])[][])} excuteList
 * @return {*}
 */
export function mergeWorkflowList(
  ctx: FreeLayoutPluginContext,
  excuteList: (FlowNodeEntity | FlowNodeEntity[])[][],
  nodeList: FlowNodeEntity[] = [],
  options: EditNodeOptions = {},
) {
  const flatArray = excuteList.map((subArr) => subArr.flat().map((_node) => _node.id));
  //   const idMap: Record<string, number[]> = {
  //     b: [1, 2],
  //     c: [3, 4],
  //     d: [1, 5],
  //     e: [5, 7],
  //   };
  const idMap: Record<string, number[]> = {};
  flatArray.forEach((ids, index) => {
    ids.forEach((id) => {
      if (!idMap[id]) {
        idMap[id] = [];
      }
      idMap[id].push(index);
    });
  });
  for (const id in idMap) {
    if (Object.prototype.hasOwnProperty.call(idMap, id)) {
      const indexList = idMap[id];
      if (indexList.length <= 1) {
        Reflect.deleteProperty(idMap, id);
      }
    }
  }

  const res = getRepeatList(idMap);

  const repeatIndexs = Array.from(res).map((str: string) => {
    return str.split(',').map(Number);
  });
  // console.log('repeatIndexs', repeatIndexs);
  const result: (FlowNodeEntity[] | FlowNodeEntity)[] = [];
  if (repeatIndexs.length) {
    let count = 0;
    repeatIndexs.forEach((indexs) => {
      // 对索引去重，避免同一个节点被添加多次
      const uniqueIndexs = Array.from(new Set(indexs));
      const tempNodes: FlowNodeEntity[] = [];
      uniqueIndexs.forEach((ii) => {
        const node = excuteList[ii][0] as FlowNodeEntity;
        tempNodes.push(node);
      });
      // 如果只有一个节点，直接存储节点；否则存储数组
      result[count] = tempNodes.length === 1 ? tempNodes[0] : tempNodes;
      count += 1;
    });
    const notRepeatNodes = excuteList.filter((_, index) => {
      const notIndex = repeatIndexs.every((item) => !item.includes(index));
      return notIndex;
    }).map((nodes) => nodes[0] as FlowNodeEntity);
    // 如果只有一个非重复节点，直接添加；否则添加数组
    if (notRepeatNodes.length === 1) {
      result.push(notRepeatNodes[0]);
    } else if (notRepeatNodes.length > 1) {
      result.push(notRepeatNodes);
    }
    console.log('mergeWorkflowList result before getExecuteWorkflowList', result);
    const finalResult = getExecuteWorkflowList(ctx, result, nodeList, 'merge', options);
    console.log('mergeWorkflowList finalResult after getExecuteWorkflowList', finalResult);
    return finalResult;
  }

  return excuteList;
}

/**
 * 获取节点的父节点
 *
 * @param {FlowNodeEntity} node
 * @return {*}
 */
const getParentNode = (ctx: FreeLayoutPluginContext, node: FlowNodeEntity) => {
  const parentNodeId = node.toJSON()?.data?.parentNodeId;
  if (parentNodeId) {
    return ctx.document.getNode(parentNodeId);
  }
  return null;
};

/**
 * 获取节点的子节点
 *
 * @export
 * @param {FreeLayoutPluginContext} ctx
 * @param {FlowNodeEntity} node
 * @return {*}
 */
export function getChildrenNode(ctx: FreeLayoutPluginContext, node: FlowNodeEntity) {
  const { data } = node.toJSON();
  if (data?.childrenNodeList) {
    return data.childrenNodeList
      .map((id: string) => ctx.document.getNode(id))
      .filter((n: FlowNodeEntity | undefined) => !!n);
  }
  return null;
}

/**
 * 检查某个节点是否被框选
 *
 * @export
 * @param {FreeLayoutPluginContext} ctx
 * @param {FlowNodeEntity} node
 * @return {*}
 */
// export function checkInSelection(ctx: FreeLayoutPluginContext, node: FlowNodeEntity) {
//   return ctx.selection.selection.some((n) => n.id === node.id);
// }
export function checkInSelection(nodeList: FlowNodeEntity[], node: FlowNodeEntity) {
  return nodeList.some((n) => n.id === node.id);
}

/**
 * 合并后，如果一条工作流前面的节点与后面的节点重复，咋把前面的节点删掉，只执行后面的
 *
 * @param {((FlowNodeEntity | FlowNodeEntity[])[])} flowList
 * @param {FlowNodeEntity} node
 * @param {('first' | 'merge')} type
 * @return {*}
 */
function checkFlowRepeatNode(flowList: (FlowNodeEntity | FlowNodeEntity[])[], node: FlowNodeEntity, type: 'first' | 'merge') {
  if (type === 'merge') {
    flowList.forEach((nodes, index) => {
      if (Array.isArray(nodes)) {
        const _i = nodes.findIndex((_n) => _n.id === node.id);
        if (_i > -1) {
          nodes.splice(_i, 1);
          // 如果删除后数组只剩一个元素，将数组替换为单个节点
          if (nodes.length === 1) {
            flowList[index] = nodes[0];
          }
        }
      }
    });
  }

  return true;
}

/**
   * 基于查找到的开始节点，向后查找完整的工作流节点list。最终结果是一个三维数组
   * 例子：
   * [
   *  [node, [node, node], node], 最内层数组代表需要并发执行的节点
   *  [node, node]
   * ]
   * @param startList
   * @returns
   */
export function getExecuteWorkflowList(
  ctx: FreeLayoutPluginContext,
  startList: (FlowNodeEntity[] | FlowNodeEntity)[],
  nodeList: FlowNodeEntity[] = [],
  type: 'first' | 'merge' = 'first',
  options: EditNodeOptions = {},
) {
  const { inPublish = false } = options;
  const executeWorkflowList: (FlowNodeEntity | FlowNodeEntity[])[][] = [];

  const _loop = (
    nodes: FlowNodeEntity[],
    res: (FlowNodeEntity | FlowNodeEntity[])[],
    index: number,
  ) => {
    const [node] = nodes;
    res[index] = nodes.length === 1 ? node : [...nodes];

    let nextNodes: FlowNodeEntity[] = nodes.reduce((target, n) => {
      const linesData = n.getData(WorkflowNodeLinesData);
      // 查找子节点的所有下一层节点
      const childrenNode = getChildrenNode(ctx, n);
      let childrenNextNode = [];
      if (childrenNode) {
        childrenNextNode = childrenNode.reduce(
          (allList: FlowNodeEntity[], curNode: FlowNodeEntity) => {
            const curLinesData = curNode.getData(WorkflowNodeLinesData);
            allList.push(...curLinesData.outputNodes);
            return allList;
          },
          [],
        );
      }

      // 过滤掉不在框选内的节点
      const nextNodeList = [
        ...linesData.outputNodes,
        ...childrenNextNode,
      ].filter((nextNode: FlowNodeEntity) => (inPublish || checkInSelection(nodeList, nextNode)) && checkFlowRepeatNode(res, nextNode, type));
      target.push(...nextNodeList);
      return target;
    }, [] as FlowNodeEntity[]);

    // 基于节点 id 去重
    const uniqueNodesMap = new Map<string, FlowNodeEntity>();
    nextNodes.forEach((node) => {
      uniqueNodesMap.set(node.id, node);
    });
    nextNodes = Array.from(uniqueNodesMap.values());

    // 如果有下一层，继续递归
    if (nextNodes.length) {
      _loop(nextNodes, res, index + 1);
    }
  };

  for (let i = 0; i < startList.length; i += 1) {
    if (!executeWorkflowList[i]) {
      executeWorkflowList[i] = [];
    }
    const nodes = startList[i];
    _loop(Array.isArray(nodes) ? nodes : [nodes], executeWorkflowList[i], 0);
  }
  console.log('getExecuteWorkflowList executeWorkflowList', executeWorkflowList);
  return executeWorkflowList;
}

/**
 * 获取工作流维度的idmap
 * @param executeWorkflowList
 * @returns
 */
export function getExecuteWorkflowListMap(
  // 目前只是一个三维数组
  executeWorkflowList: (FlowNodeEntity | FlowNodeEntity[])[][],
) {
  return executeWorkflowList.map((workflow) => {
    const tempNotice: IExecuteWorkflowItem = {
      nodeIds: [],
      isNotice: false,
    };
    workflow.forEach((node) => {
      if (Array.isArray(node)) {
        tempNotice.nodeIds.push(...node.map((item) => item.id));
      } else {
        tempNotice.nodeIds.push(node.id);
      }
    });

    return tempNotice;
  });
}

// 编辑节点
export const EditNode = editNodeFlowType as string[];

/**
 * 用于埋点事件
 * 执行链路中所有带执行按钮的编辑节点 id，按工作流展开顺序去重，多个以逗号拼接
 */
export function getRunNodeIdAllString(
  executeWorkflowList: (FlowNodeEntity | FlowNodeEntity[])[][],
): string {
  const seen = new Set<string>();
  const ids: string[] = [];
  executeWorkflowList.forEach((workflow) => {
    workflow.forEach((step) => {
      const nodes = Array.isArray(step) ? step : [step];
      nodes.forEach((n) => {
        if (!EditNode.includes(n.flowNodeType as FlowNodeTypeEnum)) return;
        if (seen.has(n.id)) return;
        seen.add(n.id);
        ids.push(n.id);
      });
    });
  });
  return ids.join(',');
}

// 输入输出节点
export const MEDIA_TYPE_LIST = inputNodeFlowType as string[];

/**
 * 查找所有为开始节点的编辑节点
 *
 * @export
 * @param {FreeLayoutPluginContext} ctx
 * @param {FlowNodeEntity[]} nodeList
 * @return {*}
 */
export function getStartEditNode(
  ctx: FreeLayoutPluginContext,
  nodeList: FlowNodeEntity[],
  options: EditNodeOptions = {},
) {
  const { inPublish = false } = options;
  const selectionEditNodeList = nodeList.filter((node) => EditNode.includes(
    (node as FlowNodeEntity).flowNodeType as FlowNodeTypeEnum,
  ));
  const result = new Set<FlowNodeEntity>();

  const _loop = (nodes: FlowNodeEntity[]) => {
    for (const node of nodes) {
      // 如果不是编辑节点，则跳过
      if (!EditNode.includes(node.flowNodeType as FlowNodeTypeEnum)) {
        // eslint-disable-next-line no-continue
        continue;
      }

      const linesData = node.getData(WorkflowNodeLinesData);
      const isStart = linesData.inputNodes.every((n) => {
        // 前置节点如果存在编辑节点，则当前节点肯定不是开始节点，直接返回false
        // 输入节点如果不在框选内，则当前节点可能为开始节点
        // 注意！ 这个 checkInSelection 的 unCheck 不需要传递，因为判断的不是传入的节点
        if (
          EditNode.includes(n.flowNodeType as FlowNodeTypeEnum)
            && (inPublish || checkInSelection(nodeList, n))
        ) {
          return false;
        }

        // 判断非编辑节点是否有父节点。如果有父节点，则上游节点一定不是开始节点，返回fasle
        const parentNode = getParentNode(ctx, n);
        if (parentNode && (inPublish || checkInSelection(nodeList, parentNode))) {
          return false;
        }

        // 此时一定是开始节点
        return true;
      });

      // 如果是开始节点，存入结果
      // 判断查找到的节点是否在选中节点中
      const inSelection = inPublish || checkInSelection(nodeList, node);

      // 开始节点不能被禁用否则就不是开始节点
      const isDisabled = useFlowStore.getState().nodeDisabledConfig[node.id]?.disabled && !inPublish;
      if (isStart && inSelection && !isDisabled) {
        result.add(node);
      } else {
        // 如果非开始节点则向上继续找
        _loop(linesData.inputNodes);
      }
    }
  };

  _loop(selectionEditNodeList);

  return Array.from(result);
}

/**
   * 处理节点列表，根据 isIncludeGroupNodeSelf 参数决定是否包含组节点本身
   * @param nodes 要处理的节点数组
   * @param isIncludeGroupNodeSelf true: 返回所有节点 + 组内节点；false: 排除组节点自身
   * @returns 处理后的节点列表
   */
export const getProcessedSelectedNodes = (
  nodes: FlowNodeEntity[],
  isIncludeGroupNodeSelf: boolean,
): FlowNodeEntity[] => {
  if (nodes.length === 0) return [];

  const resultNodes: FlowNodeEntity[] = [];
  const nodeIdSet = new Set<string>();
  nodes.forEach((node) => {
    const nodeData = node.toJSON();
    const isGroupNode = nodeData.type === FlowNodeBaseType.GROUP;
    if (isGroupNode) {
      // 如果是群组节点，添加群组内的所有节点
      const blocks = node.blocks || [];
      blocks.forEach((block: FlowNodeEntity) => {
        if (!nodeIdSet.has(block.id)) {
          nodeIdSet.add(block.id);
          resultNodes.push(block);
        }
      });
      if (isIncludeGroupNodeSelf) {
        if (!nodeIdSet.has(node.id)) {
          nodeIdSet.add(node.id);
          resultNodes.push(node);
        }
      }
      return;
    }
    // 普通节点直接添加
    if (!nodeIdSet.has(node.id)) {
      nodeIdSet.add(node.id);
      resultNodes.push(node);
    }
  });
  return resultNodes;
};
